import { GroupCondition, LanguagesEnum } from '@/api/enums';
import { equal, group } from '@/api/helpers';
import { CourseStatusAttemptEnum, MaterialType } from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { CoursesFilters } from '@/components/courses/CoursesFilters';
import { useLearnerCourses } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import CoursesComingSoonImage from '@/images/placeholders/courses-coming-soon.svg';
import CoursesNoActiveImage from '@/images/placeholders/courses-no-active.svg';
import CoursesNoCompleted from '@/images/placeholders/courses-no-completed.svg';
import { selectCurrentUser } from '@/store/features/users';
import AppButton from '@/ui/buttons/AppButton';
import { ContentCard } from '@/ui/ContentCard.tsx';
import { ScormCard } from '@/ui/scorm-card';
import { CardGridContainer, FlexContainer } from '@/ui/styled-ui';
import {
  getCourseLanguages,
  getTranslationByLanguage,
  scormColumnsWidth,
} from '@/utils/helpers';
import { debounce } from 'lodash';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledCardGridContainer = styled(CardGridContainer)`
  @media screen and (max-width: ${scormColumnsWidth.THREE}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: ${scormColumnsWidth.TWO}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: ${scormColumnsWidth.ONE}px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StyledFlexContainer = styled(FlexContainer)`
  border-radius: var(--medium-border-radius);
  background-color: var(--beige-main);
  padding: var(--default-padding);
  overflow-x: hidden;

  h2 {
    margin-top: var(--default-padding);
    margin-bottom: 0;
  }

  p {
    margin-top: var(--small-padding);
    margin-bottom: var(--default-padding);
  }
`;

const StyledImg = styled.img`
  height: auto;
  width: 100%;
`;

type LearnerContentListProps = {
  type: CourseStatusAttemptEnum.IN_PROGRESS | CourseStatusAttemptEnum.COMPLETED;
  atLeastOneCompleted?: boolean;
  isLoading?: boolean;
};

export const LearnerContentList: React.FC<LearnerContentListProps> = ({
  type,
  atLeastOneCompleted,
  isLoading,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector(selectCurrentUser);

  const [search, setSearch] = useState('');
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 500),
    [],
  );

  const { isLoading: isScormCoursesLoading, learnerCourses } =
    useLearnerCourses({
      skip: 0,
      type: type,
      search: search,
      filters: [
        group(GroupCondition.OR, [
          equal('type', MaterialType.COURSE),
          equal('type', MaterialType.CUSTOM_MATERIAL),
        ]),
      ],
      staleTime: Infinity,
    });

  useEffect(() => {
    setSearch('');
  }, [type]);

  // Used for a11y
  const [showLiveRegion, setShowLiveRegion] = useState(false);
  useEffect(() => {
    if (learnerCourses?.result?.length && learnerCourses.result.length > 0) {
      const timeout = setTimeout(() => setShowLiveRegion(true), 100);
      return () => clearTimeout(timeout);
    } else {
      setShowLiveRegion(false);
    }
  }, [learnerCourses?.result?.length]);

  return (
    <>
      <CoursesFilters onSearch={debouncedSetSearch} />
      {/* Active Courses Page when you don't have enrolled, in progress or completed courses */}
      {!isScormCoursesLoading &&
        !isLoading &&
        !learnerCourses?.result.length &&
        !atLeastOneCompleted &&
        !search &&
        type === CourseStatusAttemptEnum.IN_PROGRESS && (
          <StyledFlexContainer direction="column" className="mt-4" flex="1">
            <StyledImg
              style={{ maxHeight: '216px' }}
              src={CoursesComingSoonImage}
              alt={t('content.noContent.heading')}
            />
            <h2>{t('content.noContent.heading')}</h2>
            <p>{t('content.noContent.subheading')}</p>
          </StyledFlexContainer>
        )}

      {/* Active Courses Page when you don't have in progress courses but you have completed courses */}
      {!isScormCoursesLoading &&
        !isLoading &&
        !learnerCourses?.result.length &&
        atLeastOneCompleted &&
        !search && (
          <StyledFlexContainer direction="column" className="mt-4" flex="1">
            <StyledImg
              style={{ maxHeight: '190px' }}
              src={CoursesNoActiveImage}
              alt={t('content.noActiveContent.heading')}
            />
            <h2>{t('content.noActiveContent.heading')}</h2>
            <p>{t('content.noActiveContent.subheading')}</p>
            <AppButton
              label={t('content.completed')}
              severity="danger"
              onClick={() =>
                navigate(
                  RedirectPaths[
                    RedirectPathsEnum.LEARNER_DASHBOARD_COMPLETED
                  ](),
                )
              }
            />
          </StyledFlexContainer>
        )}

      {/* Completed Courses Page when you don't have completed courses */}
      {!isScormCoursesLoading &&
        !isLoading &&
        !learnerCourses?.result.length &&
        !atLeastOneCompleted &&
        !search &&
        type === CourseStatusAttemptEnum.COMPLETED && (
          <StyledFlexContainer direction="column" className="mt-4" flex="1">
            <StyledImg
              style={{ maxHeight: '316px' }}
              src={CoursesNoCompleted}
              alt={t('content.noCompleted.heading')}
            />
            <h2>{t('content.noCompleted.heading')}</h2>
            <p>{t('content.noCompleted.subheading')}</p>
          </StyledFlexContainer>
        )}

      {(isScormCoursesLoading || isLoading || !learnerCourses) && (
        <FlexContainer direction="column" className="mt-5 flex-1">
          <ProgressSpinner />
          <h3>{t('content.loading')}</h3>
        </FlexContainer>
      )}
      {!isScormCoursesLoading && !learnerCourses?.result.length && !!search && (
        <StyledFlexContainer direction="column" className="mt-4" flex="1">
          <h2>{t('content.search.noResults')}</h2>
        </StyledFlexContainer>
      )}
      {learnerCourses?.result.length !== 0 && (
        <div className="w-full">
          <StyledCardGridContainer role="list">
            {learnerCourses?.result.map((content) =>
              content.type === MaterialType.COURSE ? (
                <ScormCard
                  key={content.id}
                  id={content.id}
                  thumbnail={content.courseThumbnail?.signedUrl}
                  title={
                    getTranslationByLanguage(
                      content.courseTranslations,
                      currentUser?.language
                        ? (currentUser?.language as LanguagesEnum)
                        : content.defaultLanguage,
                      content.defaultLanguage,
                    )?.name ?? ''
                  }
                  language={getCourseLanguages(content.courseLanguages)}
                  attempts={content.attempts}
                  onClick={() => {
                    navigate(
                      RedirectPaths[RedirectPathsEnum.CONTENT_INFO](content.id),
                      { state: { from: location.pathname } },
                    );
                  }}
                />
              ) : (
                <ContentCard key={content.id} template={content} />
              ),
            )}
            {showLiveRegion && (
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
              >
                {t('content.list.showing', {
                  num: learnerCourses?.result.length,
                })}
              </div>
            )}
          </StyledCardGridContainer>
        </div>
      )}
    </>
  );
};
